<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getAllTrackingProperties } from '$lib/helper';
    import { isUrl } from '$lib/utils/isUrl';

    import { onMount, onDestroy } from 'svelte';

    import CalendarPopup from './CalendarPopup.svelte';
    import { CalendarProvider } from './constants';
    import Form from './FormQuestion.View.svelte';
    import { ghl } from '../_utils/ghl';
    import { sanitizeValues } from '../_utils/helpers';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Box as BoxType, Align, Size } from '$lib/types/Components/SharedTypes';
    import type { FormValues } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { OtpVerificationParams } from '$lib/types/OtpVerification';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    export let campaignId = '';

    // GHL Calendar
    export let calendarUrl = '';
    export let provider = undefined;
    export let onGHLFailed = () => console.error('GHL calendar could not be loaded...');
    export let onCalendarFormSubmit: (formValues: FormValues) => void = () => null;
    export let otpVerificationParams: OtpVerificationParams | null = null;

    // Question
    export let id = '';
    export let theme: Theme;
    export let align: Align = 'center';
    export let draft: JSONContent;
    export let wysiwygHtml: string | undefined = undefined;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    // Inputs
    export let inputs: InputData[] = [];

    // Submit Button
    export let btnId = '';
    export let btnDraft: JSONContent;
    export let btnWysiwygHtml: string | undefined = undefined;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop: boolean = false;
    export let btnSize: Size = 'medium';
    export let btnStyle: ButtonStyle = 'filled';
    export let loading = false;

    let savedFormValues: FormValues = {};
    let isPopupOpen = false;
    let iframeUrl = '';

    const isValidUrl =
        provider === CalendarProvider.GHL ? isUrl(calendarUrl) : ghl.validateUrl(calendarUrl);

    onMount(() => {
        window.addEventListener('message', handleMessageEvent);
    });

    onDestroy(() => {
        window.removeEventListener('message', handleMessageEvent);
    });

    const openGHLPopup = () => {
        if (!isValidUrl) {
            onGHLFailed();
            return;
        }

        const allTrackingProperties = getAllTrackingProperties(campaignId);

        iframeUrl = ghl.buildIframeUrl(calendarUrl, allTrackingProperties);

        isPopupOpen = true;
    };

    const closePopup = () => {
        isPopupOpen = false;
    };

    const handleEventScheduled = () => {
        closePopup();
        onSubmit(savedFormValues);
    };

    const handleMessageEvent = (event: MessageEvent) => {
        const data = event.data;

        if (!data || typeof data !== 'object') return;

        if (ghl.isBookingSuccessful(data)) {
            handleEventScheduled();
        }
    };

    const handleSubmit = async (formData: FormValues) => {
        onCalendarFormSubmit(formData);

        if (isValidUrl) {
            savedFormValues = formData;

            try {
                // Perform OTP verification before opening GHL popup
                const { otpVerification, isNewPhoneField, isPhoneVerificationCodeField } =
                    otpVerificationParams || {};

                if (otpVerification) {
                    const sanitizedValues = sanitizeValues(
                        formData,
                        isNewPhoneField,
                        isPhoneVerificationCodeField,
                    );

                    await otpVerification(sanitizedValues);
                }

                return openGHLPopup();
            } catch (error) {
                console.warn('OTP verification for GHL form failed', { error });
                return;
            }
        }

        return onSubmit(formData);
    };
</script>

<Form
    {id}
    {theme}
    {align}
    {draft}
    {wysiwygHtml}
    {color}
    {box}
    {inputs}
    {btnId}
    {btnDraft}
    {btnWysiwygHtml}
    {btnBgColor}
    {btnFullWidthOnDesktop}
    {btnColor}
    {btnAlign}
    {btnStyle}
    {btnSize}
    {loading}
    {layout}
    {language}
    {onMouseEnter}
    {transitionEffect}
    {transitionDelay}
    onSubmit={handleSubmit}
/>

<CalendarPopup isOpen={isPopupOpen} {iframeUrl} onClose={closePopup} />
